/**
 * src/hooks/useRiskAiAssessmentList.js
 *
 * The tenant's AI risk assessments as risk-ai-assess `list` returns them
 * (RAI-05; design docs/risk-ai-assessment-design.md §1.1). Its undecided rows
 * are the "decide on AI assessment" Tier 2 source of the one task list —
 * shaped by the shared riskAiAssessmentTasks (assignments.cjs), the same
 * function the server's mandatory-items list uses, so the ◎ badge, the Action
 * Centre, the Tasks page and the calendar count one item once, the same way
 * (TASKS-B-001).
 *
 * Nothing here is a copy of a decision: every refresh replaces the list, and
 * the decision on each row was derived by the server against the register at
 * read time. One store per tenant, shared by every surface.
 *
 * A failed read keeps the last list and marks the store failed
 * (useRiskAiAssessmentListFailed), so a surface can say the assessments could
 * not be loaded — a failed read never silently empties someone's owed work.
 * A 403 (a portal role, or a session that may not use the endpoint) reads as
 * "none". The plan gate does NOT apply to `list`: owed work stays visible and
 * closable after a downgrade (RISK-B-013).
 */

import { useEffect, useSyncExternalStore } from 'react';
import { listRiskAiAssessments } from '../api/riskAiAssessApi.js';

const EMPTY = [];
const stores = new Map();

function getStore(key) {
  let store = stores.get(key);
  if (!store) {
    store = {
      key,
      assessments: EMPTY,
      failed: false,
      listeners: new Set(),
      loaded: false,
      inflight: null,
    };
    stores.set(key, store);
  }
  return store;
}

function refresh(store) {
  if (store.inflight) return store.inflight;
  store.inflight = Promise.resolve()
    .then(() => listRiskAiAssessments())
    .then((res) => {
      store.assessments = Array.isArray(res?.assessments) ? res.assessments : EMPTY;
      store.failed = false;
    })
    .catch((e) => {
      if (e?.httpStatus === 403 || /^Forbidden/.test(e?.message || '')) {
        store.assessments = EMPTY;
        store.failed = false;
      } else {
        store.failed = true;
      }
    })
    .finally(() => {
      store.loaded = true;
      store.inflight = null;
      for (const l of store.listeners) l();
    });
  return store.inflight;
}

/** Re-read every held list — after an assessment is started or decided. */
export function refreshRiskAiAssessmentList() {
  return Promise.all([...stores.values()].map(refresh));
}

function useStore(session, tenant) {
  const tenantId = (typeof tenant === 'string' ? tenant : tenant?.id) || session?.tenantId;
  const role = session?.role;
  // Internal tenant roles only (RISK-B-009): a super-admin is not a tenant
  // person, and a portal role is refused by the endpoint.
  const enabled = Boolean(
    tenantId && session?.userId && role && role !== 'super-admin' && !/portal$/.test(role)
  );
  return enabled ? getStore(tenantId) : null;
}

/**
 * @param {object} session
 * @param {object|string} tenant
 * @returns {object[]} list rows { id, createdBy, createdAt, description, scopeRiskId, decision }
 */
export function useRiskAiAssessmentList(session, tenant, { refreshOnMount = false } = {}) {
  const store = useStore(session, tenant);
  const assessments = useSyncExternalStore(
    (cb) => {
      if (!store) return () => {};
      store.listeners.add(cb);
      return () => store.listeners.delete(cb);
    },
    () => (store ? store.assessments : EMPTY)
  );
  useEffect(() => {
    if (!store) return;
    if (refreshOnMount || !store.loaded) refresh(store);
  }, [store, refreshOnMount]);
  return assessments;
}

/** True while the last read failed — the list shown is then the last one read, or none. */
export function useRiskAiAssessmentListFailed(session, tenant) {
  const store = useStore(session, tenant);
  return useSyncExternalStore(
    (cb) => {
      if (!store) return () => {};
      store.listeners.add(cb);
      return () => store.listeners.delete(cb);
    },
    () => Boolean(store && store.failed)
  );
}

/**
 * How far the list can be counted (GH #7583): 'ok', 'loading' (not read yet),
 * 'failed' (the last read failed), or 'none' (this session reads no list).
 */
export function useRiskAiAssessmentListStatus(session, tenant) {
  const store = useStore(session, tenant);
  return useSyncExternalStore(
    (cb) => {
      if (!store) return () => {};
      store.listeners.add(cb);
      return () => store.listeners.delete(cb);
    },
    () => (!store ? 'none' : store.failed ? 'failed' : store.loaded ? 'ok' : 'loading')
  );
}

/** Test seam. */
export function __resetRiskAiAssessmentStores() {
  stores.clear();
}
